import { anchorBelowElement } from '@/components/ui/Menu.web';
import { createElement, useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

function clampPopoverPosition(
  anchorLeft: number,
  anchorTop: number,
  anchorBottom: number,
  width: number,
  heightEstimate: number,
): { left: number; top: number } {
  if (typeof window === 'undefined') {
    return { left: anchorLeft, top: anchorTop };
  }
  const left = Math.max(8, Math.min(anchorLeft, window.innerWidth - width - 8));
  let top = anchorTop;
  if (top + heightEstimate > window.innerHeight - 8) {
    const above = anchorBottom - heightEstimate;
    top = above >= 8 ? above : Math.max(8, window.innerHeight - heightEstimate - 8);
  }
  return { left, top };
}

function positionFromAnchor(el: HTMLElement, width: number, heightEstimate: number) {
  const rect = el.getBoundingClientRect();
  const below = anchorBelowElement(el);
  return clampPopoverPosition(
    below.left,
    below.top,
    rect.top - 4,
    width,
    heightEstimate,
  );
}

/** Renders cell editor popovers in a body portal so table overflow cannot clip them. */
export function CellPopoverPortal({
  open,
  anchorEl,
  children,
  dataAttribute,
  width = 280,
  heightEstimate = 380,
  className,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  children: ReactNode;
  dataAttribute: string;
  width?: number;
  heightEstimate?: number;
  className?: string;
}) {
  const [position, setPosition] = useState({ left: 0, top: 0 });

  useLayoutEffect(() => {
    if (!open || !anchorEl) {
      return;
    }
    setPosition(positionFromAnchor(anchorEl, width, heightEstimate));
  }, [open, anchorEl, width, heightEstimate]);

  useEffect(() => {
    if (!open || !anchorEl) {
      return;
    }
    const el = anchorEl;
    function update() {
      setPosition(positionFromAnchor(el, width, heightEstimate));
    }
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorEl, width, heightEstimate]);

  if (!open || !anchorEl || !children || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    createElement(
      'div',
      {
        [`data-${dataAttribute}`]: 'true',
        className,
        style: {
          position: 'fixed',
          left: position.left,
          top: position.top,
          width,
          maxHeight:
            typeof window !== 'undefined'
              ? Math.max(160, window.innerHeight - position.top - 8)
              : undefined,
          overflowY: 'auto',
          overflowX: 'visible',
          zIndex: 100000,
        },
        onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        onClick: (e: MouseEvent) => e.stopPropagation(),
      },
      children,
    ),
    document.body,
  );
}
